import { useEffect, useMemo, useState } from 'react';
import { Box, Select, Textarea, ActionIcon, Alert } from '@mantine/core';
import type { Field } from '../../types';
import { useMemoStore } from '../../stores/memoStore';
import { MaterialIcon } from '../common/MaterialIcon';

interface MemoInputFormProps {
  fields: Field[];
  /** 農場単位表示のとき、その農場の圃場のみをフィルタするための農場 ID。null は地区表示 */
  farmId: string | null;
  onSubmit: () => void;
}

/**
 * メモ入力フォームコンポーネント。
 *
 * 圃場を必ず選択した上で本文を入力してサーバーに保存する。
 * 投稿者は認証済みユーザーからサーバー側で自動補完される。
 */
export function MemoInputForm({ fields, farmId, onSubmit }: MemoInputFormProps) {
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const addMemo = useMemoStore((state) => state.addMemo);

  const targetFields = useMemo(
    () => (farmId ? fields.filter((f) => f.farmId === farmId) : fields),
    [fields, farmId]
  );

  const fieldOptions = targetFields
    .filter((f) => f.name !== undefined)
    .map((f) => ({
      value: f.id,
      label: f.name as string,
    }));

  // 表示対象（farmId / fields）が変わったら選択をリセットする。
  // 古い圃場 ID を保持したまま別農場へ誤投稿することを防ぐ。
  useEffect(() => {
    if (selectedFieldId && !targetFields.some((f) => f.id === selectedFieldId)) {
      setSelectedFieldId(null);
    }
  }, [targetFields, selectedFieldId]);

  const canSubmit = !!content.trim() && !!selectedFieldId && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit || !selectedFieldId) return;

    // レース対策: 送信直前に選択中の圃場が現在の表示対象に含まれるかを再確認する
    if (!targetFields.some((f) => f.id === selectedFieldId)) {
      setSelectedFieldId(null);
      setSubmitError('表示対象が変わったため圃場の再選択が必要です');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      await addMemo({ fieldId: selectedFieldId, content: content.trim() });
      // 成功時のみ入力をクリアしてフォームを閉じる。
      setContent('');
      setSelectedFieldId(null);
      onSubmit();
    } catch (error) {
      // 失敗時は入力値を保持し、エラー内容をフォーム直下に表示する。
      const msg = error instanceof Error ? error.message : 'メモの送信に失敗しました';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
      }}
    >
      <Select
        size="xs"
        placeholder="圃場を選択"
        data={fieldOptions}
        value={selectedFieldId}
        onChange={setSelectedFieldId}
      />
      <Box style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
        <Textarea
          size="xs"
          placeholder="メモを入力..."
          value={content}
          onChange={(e) => setContent(e.currentTarget.value)}
          autosize
          minRows={2}
          maxRows={4}
          style={{ flex: 1 }}
        />
        <ActionIcon
          size="md"
          variant="subtle"
          color="blue"
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          <MaterialIcon icon="send" size="sm" />
        </ActionIcon>
      </Box>
      {submitError && (
        <Alert
          color="red"
          variant="light"
          p="xs"
          withCloseButton
          onClose={() => setSubmitError(null)}
        >
          {submitError}
        </Alert>
      )}
    </Box>
  );
}
